import { MAX_DASHBOARD_DESCRIPTION, MAX_DASHBOARD_NAME } from '@clockwise/clients/dashboards';
import {
  type DashboardDefinition,
  type FilterNode,
  type FilterRule,
  parseDefinition,
  type ReportModel,
} from '@clockwise/semantic-core';
import { ApiError } from '@clockwise/shared';
import type { DateRangeValue } from '@clockwise/shared/date-range';
import { DATE_RANGE_PRESETS } from '@clockwise/shared/date-range';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Check, CopyPlus, MoreHorizontal, Pencil, Share2, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { ConfirmDialog } from '@/components/custom/confirm-dialog';
import {
  EditDetailsButton,
  NameDescriptionDialog,
} from '@/components/custom/name-description-dialog';
import { RequirePermission } from '@/components/custom/require-permission';
import { SaveFiltersDialog } from '@/components/custom/save-filters-dialog';
import { shareReach } from '@/components/custom/share-badge';
import {
  dashboardSaveQuestion,
  withDefaultView,
} from '@/components/dashboard/custom/dashboard-save';
import { DashboardView } from '@/components/dashboard/custom/dashboard-view';
import { saysSomething } from '@/components/dashboard/custom/filters';
import { PromoteTileDialog } from '@/components/dashboard/custom/promote-tile-dialog';
import { ShareDashboardDialog } from '@/components/dashboard/custom/share-dashboard-dialog';
import { SwitchDashboardMenu } from '@/components/dashboard/custom/switch-dashboard-menu';
import { replaceTile } from '@/components/dashboard/custom/tile-editing';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useUnsavedRouteGuard } from '@/hooks/use-unsaved-route-guard';
import { analyticsApi, dashboardsApi } from '@/lib/api';
import { apiErrorText } from '@/lib/errors';
import { encodeFilters, readFilters, writeFilters } from '@/lib/filter-url';
import { useMe } from '@/lib/me';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import { samePeriod } from '@/lib/saved-period';
import { describeView, fieldLabel } from '@/lib/saved-view';

/**
 * The URL is the state store.
 *
 * Period and filters live here rather than in the definition, which is what
 * makes a filtered view bookmarkable and shareable — and what keeps FR-4.8's
 * first tier honest: changing a filter is not an edit and must never be saved
 * for everybody.
 *
 * Every field `.catch`es, so a stale or hand-edited URL degrades to the
 * dashboard's own defaults instead of erroring the route.
 */
const search = z.object({
  edit: z.boolean().optional().catch(undefined),
  // The ENUM, not a bare string: `.catch` only rescues a non-string, so a
  // hand-edited or truncated link carrying `?period=lol` would otherwise be
  // cast to a preset, ride into every tile's spec, and be refused by the
  // analytics endpoint — turning one bad query param into an error on every
  // tile instead of a fall back to the dashboard's own default.
  period: z.enum(DATE_RANGE_PRESETS).optional().catch(undefined),
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
  /** The filter rules — see `filter-url.ts`. `dimension:key,key|…` in the common case. */
  f: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/_authenticated/dashboards/$dashboardId')({
  validateSearch: search,
  component: DashboardDetailRoute,
});

/**
 * ONE MOUNT PER DASHBOARD. The router reuses the route component when only
 * the id changes — the switcher, Back, Forward, a duplicate — so without the
 * key every piece of page state would carry over to the next dashboard: a chip
 * still being filled in, an edit draft the guard was told to discard, and a
 * delete confirmation that would then name, and delete, the NEW dashboard.
 * Resetting them one by one is a list every new piece of state has to join.
 */
function DashboardDetailRoute() {
  const { dashboardId } = Route.useParams();
  return <DashboardDetailPage key={dashboardId} dashboardId={dashboardId} />;
}

function DashboardDetailPage({ dashboardId }: { dashboardId: string }) {
  const params = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.dashboards.detail(dashboardId),
    queryFn: () => dashboardsApi.get(dashboardId).then((r) => r.data),
  });
  const dashboard = query.data;

  // THE WHOLE DEFINITION is the draft, not just its layout. Adding a tile,
  // renaming one and dragging one are all changes to the same object, and
  // holding three pieces of edit state would leave three chances for them to
  // disagree about what is being saved.
  //
  // `null` means "not editing" — entering edit mode seeds it from the saved
  // definition, so an abandoned edit leaves nothing behind.
  const [draft, setDraft] = useState<DashboardDefinition | null>(null);
  const mayEditReports = useHasPermission('report.edit');
  const editing = params.edit === true && draft !== null;
  const [sharing, setSharing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [renaming, setRenaming] = useState(false);
  /** A refused rename, shown in the dialog — a name collision, almost always. */
  const [renameError, setRenameError] = useState<string | null>(null);
  /** The dashboard report being saved as a custom report, by tile id. */
  const [promoting, setPromoting] = useState<string | null>(null);
  const [promoteError, setPromoteError] = useState<string | null>(null);
  const { permissions } = useMe();

  const period = useMemo<DateRangeValue>(() => {
    if (params.period || params.from) {
      return {
        preset: (params.period as DateRangeValue['preset']) ?? 'custom',
        from: params.from,
        to: params.to,
      };
    }
    const saved = dashboard?.definition.period;
    return { preset: saved?.preset ?? 'custom', from: saved?.from, to: saved?.to };
  }, [params.period, params.from, params.to, dashboard]);

  // A period that IS the saved one writes nothing, like filters that are the
  // defaults: the plain link stays plain, and follows the saved period when an
  // editor changes it.
  const savedPeriod = dashboard?.definition.period;
  const setPeriod = useCallback(
    (next: DateRangeValue) => {
      const saved = savedPeriod && samePeriod(savedPeriod, next);
      navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          period: saved ? undefined : next.preset,
          from: saved ? undefined : next.from,
          to: saved ? undefined : next.to,
        }),
        replace: true,
      });
    },
    [navigate, savedPeriod],
  );

  /**
   * The filter rules: LOADED from the URL, SAVED back to it.
   *
   * Held in state rather than read straight off the URL, because the URL
   * cannot hold everything the bar needs. A chip just added has no value yet
   * and so no place in a link, and a rule's id is not written into one — so a
   * bar reading the URL alone would drop the chip the reader is filling in,
   * and re-key every chip (closing its open panel) on each round trip.
   *
   * The URL still wins whenever it changes from OUTSIDE — back, forward, a
   * pasted link — which is what `written` tells apart: it is the last value
   * this page wrote, so a different one arrived from elsewhere.
   *
   * A link with no `f` opens on the dashboard's SAVED defaults, which arrive
   * with the dashboard — so the rules are re-read when those change too: on
   * load, and when an editor saves new ones. Read off the saved definition,
   * never the draft: a default is not in force until it is saved for everyone.
   */
  const defaults = dashboard?.definition.defaultFilters ?? NO_DEFAULTS;
  const defaultsKey = encodeFilters(defaults);
  const [filters, setFilters] = useState<readonly FilterNode[]>(() =>
    readFilters(params.f, defaults),
  );
  const written = useRef(params.f);

  // DURING RENDER, not in an effect. The tiles mount in the same render the
  // dashboard arrives in, and an effect would hand them one render of the
  // empty bar first: every tile would run unfiltered, and the reader would see
  // those numbers flash before the defaults replaced them. Compared by key,
  // because the parsed array is a new object on every refetch of an unchanged
  // dashboard, and re-reading on each would re-key every chip.
  const [readDefaults, setReadDefaults] = useState(defaultsKey);
  if (readDefaults !== defaultsKey) {
    setReadDefaults(defaultsKey);
    setFilters(readFilters(params.f, defaults));
  }

  useEffect(() => {
    if (params.f === written.current) return;
    written.current = params.f;
    setFilters(readFilters(params.f, defaults));
  }, [params.f, defaults]);

  // DEBOUNCED: a text or number chip commits per keystroke, and a navigation
  // per keystroke is a history write for every half-typed value.
  useEffect(() => {
    const encoded = writeFilters(filters, defaults);
    if (encoded === written.current) return;
    const timer = setTimeout(() => {
      written.current = encoded;
      navigate({ to: '.', search: (prev) => ({ ...prev, f: encoded }), replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [filters, defaultsKey, navigate]);

  const changeFilters = useCallback(
    (update: (prev: readonly FilterNode[]) => readonly FilterNode[]) => setFilters(update),
    [],
  );

  const duplicate = useMutation({
    mutationFn: () => dashboardsApi.duplicate(dashboardId),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards.all });
      navigate({ to: '/dashboards/$dashboardId', params: { dashboardId: data.id }, search: {} });
      toast.success(`Copied to “${data.name}”.`);
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not duplicate the dashboard.'),
  });

  const remove = useMutation({
    mutationFn: () => dashboardsApi.remove(dashboardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards.all });
      navigate({ to: '/dashboards' });
      toast.success('Dashboard deleted.');
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not delete the dashboard.'),
  });

  const save = useMutation({
    mutationFn: (definition: DashboardDefinition) =>
      dashboardsApi.patch(dashboardId, { definition }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards.detail(dashboardId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards.all });
      setDraft(null);
      navigate({ to: '.', search: (prev) => ({ ...prev, edit: undefined }), replace: true });
      toast.success('Dashboard saved.');
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not save the dashboard.'),
  });

  /**
   * The pencil's edit: `name` and `description` and NOTHING ELSE.
   *
   * The definition is left out of the payload, so renaming mid-edit neither
   * publishes the half-moved layout nor throws it away — the server patches
   * only the keys it is given, and "Save for everyone" stays the one thing that
   * writes tiles. Mirrors the pencil on a saved report's heading.
   */
  const rename = useMutation({
    mutationFn: ({ name, description }: { name: string; description: string }) =>
      dashboardsApi.patch(dashboardId, { name, description: description || null }),
    onSuccess: ({ data }) => {
      // The response IS the new detail, and only the LISTS name it besides.
      // Not `dashboards.all`: every tile's data is keyed under that prefix, and
      // a rename changes no tile's question.
      queryClient.setQueryData(queryKeys.dashboards.detail(dashboardId), data);
      queryClient.invalidateQueries({ queryKey: [...queryKeys.dashboards.all, 'list'] });
      setRenaming(false);
      setRenameError(null);
      toast.success(`Saved “${data.name}”`);
    },
    // INLINE, and the dialog stays open: a name collision is answered by
    // editing the box that is already on screen.
    onError: (err) => setRenameError(apiErrorText(err, 'Could not save the dashboard.')),
  });

  /**
   * "Save as custom report". The server acts on the SAVED dashboard and
   * answers with it, so the answer is folded in two places: the cached detail
   * (what everybody sees), and — while editing — the one tile in the draft,
   * leaving every other unsaved change exactly as it was.
   */
  const promote = useMutation({
    mutationFn: (input: { tileId: string; name: string; mode: 'move' | 'copy' }) =>
      dashboardsApi.promoteTile(dashboardId, input.tileId, { name: input.name, mode: input.mode }),
    onSuccess: ({ data }, input) => {
      queryClient.setQueryData(queryKeys.dashboards.detail(dashboardId), data.dashboard);
      queryClient.invalidateQueries({ queryKey: queryKeys.reportDefinitions.all });
      const saved = parseDefinition(data.dashboard.definition).tiles.find(
        (t) => t.id === input.tileId,
      );
      if (saved) setDraft((prev) => (prev ? replaceTile(prev, saved) : prev));
      setPromoting(null);
      setPromoteError(null);
      toast.success(`Saved “${data.report.name}” to Custom Reports.`);
    },
    // INLINE, and the dialog stays open: a name collision is answered by
    // editing the box that is already on screen.
    onError: (err) => setPromoteError(apiErrorText(err, 'Could not save the report.')),
  });

  /**
   * "Save for everyone" — asking first when the bar differs from the saved
   * view. The bar is the viewer's, the editor's included, so whether their
   * period and filters become what everyone opens on is a question, not a
   * side effect of saving the tiles.
   */
  const models = useQuery({
    queryKey: queryKeys.analytics.models(),
    queryFn: ({ signal }) => analyticsApi.models(signal).then((r) => r.data),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const [askFilters, setAskFilters] = useState<ReturnType<typeof dashboardSaveQuestion>>(null);
  const saveForEveryone = () => {
    if (!draft || !dashboard) return;
    const change = dashboardSaveQuestion({
      draft,
      saved: dashboard.definition,
      filters,
      period,
    });
    if (change) setAskFilters(change);
    else save.mutate(draft);
  };
  const labelOf = (field: string) => fieldLabel(models.data as ReportModel[] | undefined, field);

  // A layout the editor moved but has not saved is work that vanishes on a
  // navigation. `useUnsavedRouteGuard` is the route-level counterpart of the
  // sheet guard — it blocks back/forward, sidebar links and programmatic
  // `navigate()` alike.
  //
  // Compared as JSON rather than by identity: `setDraft` runs on every layout
  // change RGL reports, including the reflow it performs on mount, so identity
  // would call an untouched dashboard dirty the moment it was opened. Key order
  // ignored (`stableJson`), so an edit that is undone compares equal again.
  const dirty =
    editing &&
    draft !== null &&
    !!dashboard &&
    stableJson(draft) !== stableJson(dashboard.definition);
  const { blockerDialog } = useUnsavedRouteGuard(
    // SCOPED TO GENUINE PAGE-LEAVES. Every filter and period change is a
    // `navigate()` to this same route, and blocking those would prompt to
    // discard on every click of the filter bar.
    ({ current, next }) => dirty && current.pathname !== next.pathname,
    {
      title: 'Discard layout changes?',
      description: 'The tiles you moved have not been saved for everyone yet.',
    },
    () => dirty,
  );

  if (query.isPending) {
    return (
      <PageShell>
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }

  if (query.isError || !dashboard) {
    return (
      <PageShell>
        <PageHeader title="Dashboard" />
        <p role="alert" className="text-sm text-destructive">
          {query.error instanceof ApiError ? query.error.message : 'Could not open this dashboard.'}
        </p>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        // TITLED, because it truncates: a long name is clipped before it can
        // push the switcher off the row, and the tooltip is where the rest is.
        title={
          <span className="block truncate" title={dashboard.name}>
            {dashboard.name}
          </span>
        }
        titleAction={
          // ONE tight group, so the header's own gap does not open between
          // the two icons. The chevron goes FIRST: `Title ▾` reads as "this
          // title is a menu"; with the pencil in between, it read as a menu
          // of the pencil's.
          <div className="flex shrink-0 items-center">
            <SwitchDashboardMenu currentId={dashboardId} />
            {/* Owner-only, because `PATCH` is. Beside the title rather than
                in the `⋯`, where the report page puts it too: the pencil edits
                the words it sits next to. */}
            {dashboard.owned && permissions.has('dashboard.edit') && (
              <EditDetailsButton
                onClick={() => {
                  setRenameError(null);
                  setRenaming(true);
                }}
              />
            )}
          </div>
        }
        subtitle={dashboard.description ?? undefined}
      >
        {editing ? (
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setDraft(null);
                navigate({
                  to: '.',
                  search: (prev) => ({ ...prev, edit: undefined }),
                  replace: true,
                });
              }}
            >
              <X className="size-4" aria-hidden="true" />
              Cancel
            </Button>
            <Button disabled={save.isPending || !draft} onClick={saveForEveryone}>
              <Check className="size-4" aria-hidden="true" />
              Save for everyone
            </Button>
          </>
        ) : (
          // Owner-only: a recipient may open and duplicate, never edit. The
          // server refuses either way; this keeps a doomed button off screen.
          <>
            {dashboard.owned && (
              <RequirePermission permission="dashboard.edit">
                <Button
                  variant="outline"
                  onClick={() => {
                    setDraft(dashboard.definition);
                    navigate({
                      to: '.',
                      search: (prev) => ({ ...prev, edit: true }),
                      replace: true,
                    });
                  }}
                >
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit
                </Button>
              </RequirePermission>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Dashboard actions">
                  <MoreHorizontal className="size-4" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* Duplicating is the one mutation a RECIPIENT may make — it is
                    what keeps a shared dashboard from being a dead end. */}
                <RequirePermission permission="dashboard.edit">
                  <DropdownMenuItem
                    disabled={duplicate.isPending}
                    onSelect={() => duplicate.mutate()}
                  >
                    <CopyPlus className="size-4" aria-hidden="true" />
                    Duplicate
                  </DropdownMenuItem>
                </RequirePermission>
                {/* The owner, or a recipient whose share lets them reshare —
                    `dashboard.share` either way. The server answers in
                    `canShare`; the fallback is for an API older than that. */}
                {(dashboard.canShare ??
                  (dashboard.owned && permissions.has('dashboard.share'))) && (
                  <DropdownMenuItem onSelect={() => setSharing(true)}>
                    <Share2 className="size-4" aria-hidden="true" />
                    Share
                  </DropdownMenuItem>
                )}
                {dashboard.owned && permissions.has('dashboard.edit') && (
                  <DropdownMenuItem
                    variant="destructive"
                    disabled={remove.isPending}
                    onSelect={() => setConfirmingDelete(true)}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </PageHeader>

      {sharing && (
        <ShareDashboardDialog dashboard={dashboard} open={sharing} onOpenChange={setSharing} />
      )}

      {renaming && (
        <NameDescriptionDialog
          open
          noun="dashboard"
          maxName={MAX_DASHBOARD_NAME}
          maxDescription={MAX_DASHBOARD_DESCRIPTION}
          // Not dismissable mid-flight, so the dialog cannot be closed out from
          // under a save that is about to succeed.
          onOpenChange={(open) => {
            if (!open && !rename.isPending) setRenaming(false);
          }}
          heading="Name and description"
          blurb={
            dashboard.shares.length > 0
              ? 'Saved as soon as you press Save — including for everyone this dashboard is shared with.'
              : 'How you’ll find this dashboard again, and what anyone you share it with will read.'
          }
          suggestion={dashboard.name}
          description={dashboard.description ?? ''}
          error={renameError}
          busy={rename.isPending}
          onSubmit={(name, description) => {
            // Nothing changed — Enter on the preselected name is how people
            // close this after reading a long title. Not a write, and not an
            // audit entry.
            if (name === dashboard.name && description === (dashboard.description ?? '')) {
              setRenaming(false);
              return;
            }
            rename.mutate({ name, description });
          }}
          submitLabel="Save"
        />
      )}

      <ConfirmDialog
        open={confirmingDelete}
        title={`Delete “${dashboard.name}”?`}
        description={
          dashboard.shares.length > 0
            ? `This dashboard is shared with ${shareReach(dashboard.shares)}. Deleting it removes it from their list too, and can’t be undone. Custom reports its tiles link to aren’t deleted.`
            : 'This can’t be undone. Custom reports its tiles link to aren’t deleted.'
        }
        confirmLabel="Delete"
        destructive
        confirmDisabled={remove.isPending}
        onConfirm={() => {
          setConfirmingDelete(false);
          remove.mutate();
        }}
        onCancel={() => setConfirmingDelete(false)}
      />

      {blockerDialog}

      {askFilters && draft && (
        <SaveFiltersDialog
          open
          noun="dashboard"
          changed={askFilters.changed}
          saved={describeView({ filters: draft.defaultFilters, period: draft.period }, labelOf)}
          current={describeView(askFilters, labelOf)}
          saving={save.isPending}
          onSaveMine={() => {
            setAskFilters(null);
            save.mutate(withDefaultView(draft, askFilters));
          }}
          onKeepSaved={() => {
            setAskFilters(null);
            save.mutate(draft);
          }}
          onCancel={() => setAskFilters(null)}
        />
      )}

      {promoting && (
        <PromoteTileDialog
          open
          // Keyed by tile, so the name box starts from THIS tile's title.
          key={promoting}
          title={tileTitle(draft ?? dashboard.definition, promoting)}
          unsaved={tileChanged(draft, dashboard.definition, promoting)}
          barFiltered={filters.some(saysSomething)}
          busy={promote.isPending}
          error={promoteError}
          onSubmit={({ name, mode }) => promote.mutate({ tileId: promoting, name, mode })}
          onOpenChange={(open) => {
            if (!open) {
              setPromoting(null);
              setPromoteError(null);
            }
          }}
        />
      )}

      <DashboardView
        // The DRAFT while editing, so every change is visible where it will
        // land. Rendering the saved definition and overlaying edits would be a
        // second view of the same object — the drift `ReportView`'s own doc
        // comment warns about, one level up.
        definition={editing && draft ? draft : dashboard.definition}
        period={period}
        onPeriodChange={setPeriod}
        filters={filters}
        onFiltersChange={changeFilters}
        onDefinitionChange={editing ? setDraft : undefined}
        mayEditReports={mayEditReports}
        onPromoteTile={editing ? setPromoting : undefined}
      />
    </PageShell>
  );
}

/** One empty list, so a dashboard with no defaults does not hand out a new one per render. */
const NO_DEFAULTS: readonly FilterRule[] = [];

/** A tile's title, for the "Save as custom report" name box. */
function tileTitle(definition: DashboardDefinition, tileId: string): string {
  return definition.tiles.find((t) => t.id === tileId)?.title ?? '';
}

/**
 * Does the draft's copy of this tile differ from the saved one?
 *
 * Promoting acts on the SAVED tile, so a tile edited since the last save would
 * be promoted as it WAS — the version on screen silently lost. Compared as JSON
 * for the same reason the page's own dirty check is: identity changes on every
 * layout event.
 */
function tileChanged(
  draft: DashboardDefinition | null,
  saved: DashboardDefinition,
  tileId: string,
): boolean {
  if (!draft) return false;
  const a = draft.tiles.find((t) => t.id === tileId);
  const b = saved.tiles.find((t) => t.id === tileId);
  if (!a || !b) return true;
  // Position is not part of the report; moving the tile does not block saving it.
  return stableJson({ ...a, layout: null }) !== stableJson({ ...b, layout: null });
}

/**
 * JSON with object keys SORTED, so key order cannot make two equal tiles
 * differ: the draft's tiles come out of the schema parse in schema order, the
 * saved ones out of Postgres jsonb, which reorders keys.
 */
function stableJson(value: unknown): string {
  return JSON.stringify(value, (_key, v) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v).sort(([x], [y]) => (x < y ? -1 : x > y ? 1 : 0)))
      : v,
  );
}
